/**
 * List view settings — persisted view mode, grouping, sorting and filters for the list detail page.
 *
 * Mirrors `features/decks/state/deckEditorSettings.ts`. The search query stays ephemeral by design.
 */
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';

import type { AvailabilityFilterValue } from '@/components/AvailabilityFilter/AvailabilityFilter';
import type { ListPriority } from '@/types/lists';

export type ListViewMode = 'table' | 'list' | 'grid';

/** `none` renders one flat set with no section headers. */
export type ListGroupBy = 'none' | 'priority' | 'type' | 'cmc' | 'color_identity';

export type ListSortField = 'name' | 'type' | 'priority' | 'quantity' | 'owned' | 'available' | 'price';
export type ListSortDirection = 'asc' | 'desc';

export interface ListSortState {
  field: ListSortField;
  direction: ListSortDirection;
}

export const LIST_GROUP_BY_OPTIONS: { value: ListGroupBy; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'type', label: 'Type' },
  { value: 'priority', label: 'Priority' },
  { value: 'cmc', label: 'Mana Cost' },
  { value: 'color_identity', label: 'Color' },
];

export const LIST_SORT_OPTIONS: { value: ListSortField; label: string }[] = [
  { value: 'name', label: 'Name' },
  { value: 'type', label: 'Type' },
  { value: 'priority', label: 'Priority' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'owned', label: 'Owned' },
  { value: 'available', label: 'Available' },
  { value: 'price', label: 'Price' },
];

export const DEFAULT_LIST_SORT: ListSortState = { field: 'name', direction: 'asc' };

// ============================================================================
// Table columns
// ============================================================================

/** Optional table columns. Quantity, image, name and remove are always shown. */
export type ListColumnKey = 'type' | 'priority' | 'notes' | 'ownership' | 'price' | 'set';

export type ListColumnVisibility = Record<ListColumnKey, boolean>;

export const LIST_COLUMN_ORDER: ListColumnKey[] = ['set', 'type', 'priority', 'notes', 'ownership', 'price'];

export const LIST_COLUMN_LABELS: Record<ListColumnKey, string> = {
  set: 'Set',
  type: 'Type',
  priority: 'Priority',
  notes: 'Notes',
  ownership: 'Ownership',
  price: 'Price',
};

const LIST_COLUMN_WIDTHS: Record<ListColumnKey, string> = {
  set: '6rem',
  type: 'minmax(7rem, 1fr)',
  priority: '7rem',
  notes: 'minmax(8rem, 1.2fr)',
  ownership: '10rem',
  price: '6.5rem',
};

export const DEFAULT_LIST_COLUMN_VISIBILITY: ListColumnVisibility = {
  set: false,
  type: true,
  priority: true,
  notes: true,
  ownership: true,
  price: true,
};

/** Fixed leading cells (quantity, image, name) and trailing remove cell. */
export function buildListGridTemplateColumns(visibility: ListColumnVisibility): string {
  const columns = ['3.5rem', '3rem', 'minmax(10rem, 1.6fr)'];
  for (const column of LIST_COLUMN_ORDER) {
    if (visibility[column]) columns.push(LIST_COLUMN_WIDTHS[column]);
  }
  columns.push('2.5rem');
  return columns.join(' ');
}

// ============================================================================
// Atoms
// ============================================================================

export const listViewModeAtom = atomWithStorage<ListViewMode>('mtgcraft:list-view-mode', 'table');

export const listGroupByAtom = atomWithStorage<ListGroupBy>('mtgcraft:list-group-by', 'none');

export const listSortAtom = atomWithStorage<ListSortState>('mtgcraft:list-sort', DEFAULT_LIST_SORT);

/** Empty means no priority filter. */
export const listPriorityFilterAtom = atomWithStorage<ListPriority[]>('mtgcraft:list-priority-filter', []);

export const listAvailabilityFilterAtom = atomWithStorage<AvailabilityFilterValue>('mtgcraft:list-availability-filter', 'all');

export const listColumnVisibilityAtom = atomWithStorage<ListColumnVisibility>(
  'mtgcraft:list-column-visibility',
  DEFAULT_LIST_COLUMN_VISIBILITY,
);

/** Ephemeral list quick-search query (NOT persisted — resets on reload). */
export const listSearchQueryAtom = atom<string>('');
